import React, { useMemo, useState } from "react"

import { Empty, Flex, Spin, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"

import ButtonComponent from "../../../../Components/Button"
import { countryFlagsByCountryCode } from "../../../../Components/Filters/filterOptionsData"
import Private from "../../../../Components/Private"
import SelectComponent from "../../../../Components/Select"
import TableComponent from "../../../../Components/Table"
import Images from "../../../../Images"
import { useCountryOlympicsLocked } from "../../hooks/useCountryOlympicsLocked"
import { byCountryComingSoonData, byCountryEmptyData } from "../../utils/emptyData"
import HealthBanner from "../HealthBanner"
import ShopifyComingSoonBanner from "../ShopifyComingSoonBanner"
import { formatAmount, formatCount, getColumnsByCountry, getRowPercentage } from "../Table/columns"
import { createTableSummary } from "../Table/tableSummary"
import PercentageBar from "./PercentageBar"

import "./styles.less"

const { Title, Text } = Typography

const ByCountryBlock = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useSelector((state) => state.nav.theme)
  const isMobile = useSelector((state) => state.nav.isMobile)
  const dataByCardTypeFetching = useSelector((state) => state.health.dataByCountryFetching)
  const dataByCountry = useSelector((state) => state.health.dataByCountry)
  const { is_business_health_available } = useSelector((state) => state.health.mainInfo)
  const isLocked = useCountryOlympicsLocked()
  const { result, summary, comparison_period, comparison_summary } = isLocked
    ? byCountryComingSoonData
    : is_business_health_available
      ? dataByCountry
      : byCountryEmptyData
  const comparisonLabel = comparison_period?.label
  const [mobileColumn, setMobileColumn] = useState("transactions")

  const columns = useMemo(() => getColumnsByCountry({ t }), [t])

  const tableSettings = useMemo(
    () => ({
      scroll: result?.length > 0 ? { x: "auto", y: isMobile ? 350 : undefined } : {}
    }),
    [result, isMobile]
  )

  const CustomRow = (props) => {
    const percentage = props["data-percentage"] || 0
    return (
      <PercentageBar percentage={percentage} theme={theme}>
        <tr {...props} />
      </PercentageBar>
    )
  }

  const tableComponents = useMemo(
    () => ({
      body: {
        row: CustomRow
      }
    }),
    [result, theme]
  )

  const tableSummary = useMemo(() => createTableSummary(t), [t])

  const tableData = useMemo(() => (Array.isArray(result) ? result.slice(0, 5) : []), [result])
  const hasTableData = tableData.length > 0

  const mobileColumnOptions = useMemo(
    () => [
      { label: t("Transactions"), value: "transactions" },
      { label: t("Chargebacks"), value: "chargebacks" },
      { label: t("Alerts"), value: "alerts" },
      { label: t("Chargeback rate"), value: "chargeback_rate" },
      { label: t("Prevention rate"), value: "prevention_rate" }
    ],
    [t]
  )

  const selectedMobileColumnLabel =
    mobileColumnOptions.find((option) => option.value === mobileColumn)?.label || t("Column")

  const navigateToFullTable = () => {
    navigate("breakdown_by_country")
  }

  const renderMobileValueCell = (record) => {
    switch (mobileColumn) {
      case "transactions":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{formatCount(record?.transactions_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(record?.transactions_amount)}</Private>
            </Text>
          </Flex>
        )
      case "chargebacks":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{formatCount(record?.chargebacks_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(record?.chargebacks_amount)}</Private>
            </Text>
          </Flex>
        )
      case "alerts":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{formatCount(record?.alerts_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(record?.alerts_amount)}</Private>
            </Text>
          </Flex>
        )
      case "chargeback_rate":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{record?.chargeback_rate}%</Text>
            <Text type="secondary" strong>
              {record?.chargeback_rate_without_us}% {t("without us")}
            </Text>
          </Flex>
        )
      case "prevention_rate":
        return <Text strong>{record?.prevention_rate}%</Text>
      default:
        return null
    }
  }

  const renderMobileSummaryValue = (data) => {
    switch (mobileColumn) {
      case "transactions":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{formatCount(data?.transactions_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(data?.transactions_amount)}</Private>
            </Text>
          </Flex>
        )
      case "chargebacks":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{formatCount(data?.chargebacks_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(data?.chargebacks_amount)}</Private>
            </Text>
          </Flex>
        )
      case "alerts":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{formatCount(data?.alerts_count)}</Text>
            <Text type="secondary" strong>
              <Private>{formatAmount(data?.alerts_amount)}</Private>
            </Text>
          </Flex>
        )
      case "chargeback_rate":
        return (
          <Flex gap={2} vertical={true} align="flex-end">
            <Text strong>{data?.chargeback_rate || 0}%</Text>
            <Text type="secondary" strong>
              {data?.chargeback_rate_without_us || 0}% {t("without us")}
            </Text>
          </Flex>
        )
      case "prevention_rate":
        return <Text strong>{data?.prevention_rate || 0}%</Text>
      default:
        return null
    }
  }

  return (
    <Flex gap={12} vertical={true} className="country-block-wrap">
      <Flex gap={24} vertical={true} className="card-block-with-table country-block">
        <Spin spinning={dataByCardTypeFetching}>
          <Flex
            align="center"
            justify="space-between"
            style={{ marginBottom: 16, padding: "0 16px" }}
          >
            <Title level={5} style={{ marginBottom: 0, fontWeight: 600 }}>
              {t("Breakdown by Country")}
            </Title>

            {is_business_health_available && !isMobile && !isLocked && (
              <ButtonComponent onClick={navigateToFullTable}>{t("View All")}</ButtonComponent>
            )}
          </Flex>

          {isMobile ? (
            <Flex vertical={true} gap={12} className="by-country-mobile-table">
              <div className="by-country-mobile-table__select-wrap">
                <SelectComponent
                  size="large"
                  value={mobileColumn}
                  onChange={setMobileColumn}
                  options={mobileColumnOptions}
                  style={{ width: "100%" }}
                />
              </div>

              <div className="by-country-mobile-table__header">
                <Text type="secondary">{t("Country")}</Text>
                <Text type="secondary" className="by-country-mobile-table__header-right">
                  {selectedMobileColumnLabel}
                </Text>
              </div>

              {hasTableData ? (
                <>
                  {tableData.map((record, index) => {
                    const code = record?.country_code?.toUpperCase()
                    const image = countryFlagsByCountryCode?.[code] || null
                    const isLastRow = index === tableData.length - 1

                    return (
                      <div
                        key={record.country}
                        className={`by-country-mobile-table__row${isLastRow ? " by-country-mobile-table__row--last" : ""}`}
                      >
                        <Flex gap={8} align="center" className="by-country-mobile-table__left">
                          {image && <img src={image} alt={record.country} width={20} height={20} />}
                          <Text strong>{record.country}</Text>
                        </Flex>
                        <div className="by-country-mobile-table__right">
                          {renderMobileValueCell(record)}
                        </div>
                      </div>
                    )
                  })}

                  <div className="by-country-mobile-table__summary">
                    <Text strong className="by-country-mobile-table__summary-title">
                      {t("Summary")}
                    </Text>
                    <div className="by-country-mobile-table__right">
                      {renderMobileSummaryValue(summary)}
                    </div>
                  </div>
                  {comparison_summary && (
                    <div className="by-country-mobile-table__summary by-country-mobile-table__summary--compare">
                      <Text strong className="by-country-mobile-table__summary-title">
                        {t("Compare")} {comparisonLabel || t("previous period")}
                      </Text>
                      <div className="by-country-mobile-table__right">
                        {renderMobileSummaryValue(comparison_summary)}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="by-country-mobile-table__empty">
                  <Empty
                    image={Images.no_data}
                    imageStyle={{ width: 64, height: 40 }}
                    description={t("No Data")}
                  />
                </div>
              )}
            </Flex>
          ) : (
            <TableComponent
              columns={columns}
              rowKey={"country"}
              loading={dataByCardTypeFetching}
              dataSource={tableData}
              scroll={tableSettings.scroll}
              showSizeChanger={false}
              withOutPagination={true}
              components={tableComponents}
              onRow={(record, index) => ({
                "data-percentage": getRowPercentage(record, result)
              })}
              summary={() =>
                tableSummary(summary, result, undefined, comparison_summary, comparisonLabel)
              }
            />
          )}
        </Spin>
        {!isLocked && <HealthBanner />}
        {isLocked && (
          <ShopifyComingSoonBanner
            title="See where your chargebacks and alerts come from by country"
            contentMarginBottom={50}
          />
        )}
      </Flex>

      {is_business_health_available && isMobile && !isLocked && (
        <ButtonComponent
          className="country-view-all-btn"
          style={{ width: "100%" }}
          onClick={navigateToFullTable}
        >
          {t("View All")}
        </ButtonComponent>
      )}
    </Flex>
  )
}

export default React.memo(ByCountryBlock)
